import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { showDialog } from '../../ui/PromptDialog';

export type ClanSummary = {
  id: number;
  tag: string;
  name: string;
  frameId: number;
  iconId: number;
  frameColor: string;
  iconColor: string;
  description: string;
  status: number;
  xpRequirement: number;
  masteryRequirement: number;
  clanXp: number;
  clanMastery: number;
  memberCount: number;
  leaderId: number;
  leaderUsername?: string;
  xpRank?: number;
  masteryRank?: number;
};

export type AccountClan = {
  clan: ClanSummary;
  role: number;
  contributedXp: number;
};

export type AccountState = {
  id: number | null;
  email: string;
  username: string;
  clan: AccountClan | null;
  clanCooldownUntil: string | null;
  isLoggedIn: boolean;
  secret: string;
  gems: number;
  mastery: number;
  tokens: number;
  skins: { equipped: number; owned: number[] };
  is_v1: boolean;
  xp: number;
  recovered: boolean;
  profiles: { equipped: number; owned: number[] };
  themes: { equipped: number; owned: number[] };
  bio: string;
  tags: { tags: string[]; colors: string[] };
  isCrazygames: boolean;
  crazygamesUserId?: string;
  dailyLogin : {
    streak: number;
    bestStreak: number;
    claimedTo: number;
    claimableTo: number;
    checkedIn: number;
    xpBonus: number | null;
    playtime: number;
  };
}

const initialState: AccountState = {
  id: null,
  email: '',
  username: '',
  clan: null,
  clanCooldownUntil: null,
  secret: '',
  isLoggedIn: false,
  gems: 0,
  mastery: 0,
  tokens: 0,
  skins: { equipped: 1, owned: [1] },
  is_v1: false,
  xp: 0,
  recovered: false,
  profiles: { equipped: 1, owned: [1] },
  themes: { equipped: 1, owned: [1] },
  bio: '',
  tags: { tags: [], colors: [] },
  isCrazygames: false,
  crazygamesUserId: undefined,
  dailyLogin: {
    streak: 0,
    bestStreak: 0,
    claimedTo: 0,
    claimableTo: 0,
    checkedIn: 0,
    xpBonus: null,
    playtime: 0,
  },
};

// Async Thunks
export const logoutAsync = createAsyncThunk(
  'account/logout',
  async (_, { dispatch }) => {

    try {
      console.log('Clearing secret');
      window.localStorage.removeItem('secret');
    } catch (e) {
      console.log('Failed to clear secret', e);
    }

    dispatch(clearAccount());
  }
);

export const updateAccountAsync = createAsyncThunk(
  'account/updateAccount',
  (_, { getState, dispatch }) => {
    return new Promise((resolve, reject) => {
    const state: any = getState();
    api.post(`${api.endpoint}/profile/getPrivateUserInfo`, {}, (response: any) => {
    if (response.error) {
      void showDialog(response.error, 'Account');
      reject(response.error);
    } else if (response.account) {
      response.account.secret = state.account.secret;
      dispatch(setAccount(response.account));
      window.phaser_game?.events.emit('tokenUpdate', state.account.secret);
      resolve(response.account);
    }
  }, state.account.secret);
  });
  }
);

export const changeNameAsync = createAsyncThunk(
  'account/changeName',
  async (newUsername: string, { getState, dispatch }) => {
    // const state: any = getState();
    try {
      const response = await api.postAsync(`${api.endpoint}/auth/change-username?now=${Date.now()}`, {
        newUsername
      });

      if (response.error) {
        await showDialog(response.error, 'Change Name');
      } else if (response.success) {
        await showDialog('Username changed successfully.', 'Change Name');
        // Dispatching actions to update name and token in the state
        dispatch(setName(newUsername));
        dispatch(setSecret(response.secret));
      }
    } catch (error) {
      // Handle any other errors, such as network issues
      console.error(error);
      await showDialog('Could not change your name.', 'Change Name');
    }
  }
);

export const changeBioAsync = createAsyncThunk(
  'account/changeBio',
  async (newUserbio: string, { getState, dispatch }) => {
    // const state: any = getState();
    try {
      const response = await api.postAsync(`${api.endpoint}/auth/change-userbio?now=${Date.now()}`, {
        newUserbio
      });

      if (response.error) {
        await showDialog(response.error, 'Change Bio');
      } else if (response.success) {
        await showDialog('Bio changed successfully.', 'Change Bio');
        // Dispatching actions to update bio and token in the state
        dispatch(setBio(newUserbio));
        dispatch(setSecret(response.secret));
      }
    } catch (error) {
      // Handle any other errors, such as network issues
      console.error(error);
      await showDialog('Could not change your bio.', 'Change Bio');
    }
  }
);

export const claimDailyLoginAsync = createAsyncThunk(
  'account/claimDailyLogin',
  async (count: number | undefined, { getState, dispatch }) => {
    const state: any = getState();
    try {
      const response = await api.postAsync(`${api.endpoint}/auth/claim-daily-login?now=${Date.now()}`, count ? { count } : {});

      if (response.error) {
        await showDialog(response.error, 'Daily Rewards');
      } else if (response.success) {
        const currentAccount = state.account;
        dispatch(setAccount({
          ...currentAccount,
          dailyLogin: response.dailyLogin,
          gems: response.gems !== undefined ? response.gems : currentAccount.gems,
          mastery: response.mastery !== undefined ? response.mastery : currentAccount.mastery,
          skins: response.skins !== undefined ? response.skins : currentAccount.skins,
        }));
      }
    } catch (error) {
      console.error(error);
      await showDialog('Could not claim your daily reward.', 'Daily Rewards');
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    clearAccount: (state) => {
      state.id = null;
      state.email = '';
      state.username = '';
      state.clan = null;
      state.clanCooldownUntil = null;
      state.secret = '';
      state.gems = 0;
      state.mastery = 0;
      state.tokens = 0;
      state.isLoggedIn = false;
      state.skins = { equipped: 1, owned: [1] };
      window.phaser_game?.events.emit('tokenUpdate', '');
      state.is_v1 = false;
      state.xp = 0;
      state.recovered = false;
      state.profiles = { equipped: 1, owned: [1] };
      state.themes = { equipped: 1, owned: [1] };
      state.bio = '';
      state.tags = { tags: [], colors: [] };
      state.isCrazygames = false;
      state.crazygamesUserId = undefined;
      state.dailyLogin = {
        streak: 0,
        bestStreak: 0,
        claimedTo: 0,
        claimableTo: 0,
        checkedIn: 0,
        xpBonus: null,
        playtime: 0,
      };
    },
    setAccount: (state, action) => {
      state.id = action.payload.id ?? state.id;
      state.email = action.payload.email;
      state.username = action.payload.username;
      state.clan = action.payload.clan ?? null;
      state.clanCooldownUntil = action.payload.clanCooldownUntil ?? null;
      state.isLoggedIn = true;
      const previousToken = state.secret;
      state.secret = action.payload.secret;
      state.gems = action.payload.gems;
      state.mastery = action.payload.mastery;
      state.tokens = action.payload.tokens;
      state.skins = action.payload.skins;
      state.is_v1 = action.payload.is_v1;
      state.xp = action.payload.xp;
      state.recovered = action.payload.recovered;
      state.profiles = action.payload.profiles;
      state.themes = action.payload.themes ?? { equipped: 1, owned: [1] };
      state.bio = action.payload.bio;
      state.tags = action.payload.tags;
      state.isCrazygames = action.payload.isCrazygames;
      state.crazygamesUserId = action.payload.crazygamesUserId;
      state.dailyLogin = action.payload.dailyLogin || state.dailyLogin;
      if (previousToken !== state.secret) {
        console.log('Token updated');
        window.phaser_game?.events.emit('tokenUpdate', state.secret);

        try {
          window.localStorage.setItem('secret', state.secret);
        } catch (e) {
          console.log('Error setting secret', e);
        }
      }
    },
    setName: (state, action) => {
      state.username = action.payload;
    },
    setClan: (state, action) => {
      state.clan = action.payload;
    },
    clearClan: (state) => {
      state.clan = null;
    },
    setBio: (state, action) => {
      state.bio = action.payload;
    },
    setSecret: (state, action) => {
      state.secret = action.payload;
      console.log('Token updated');

      if (state.secret) {
        window.phaser_game?.events.emit('tokenUpdate', state.secret);
      }

      try {
        window.localStorage.setItem('secret', state.secret);
      } catch (e) {
        console.log('Error setting secret', e);
      }
    },
    setDailyLogin: (state, action) => {
      state.dailyLogin = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle async thunks here if needed
  },
});

export const { setAccount, clearAccount, setName, setClan, clearClan, setBio, setSecret, setDailyLogin } = accountSlice.actions;
export default accountSlice.reducer;
